import React, { useState } from 'react';
import { Box, Button, Collapse, Link, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  Definition, FichierReponse, Question, Reponses, StatutInstance, ValeurReponse,
  formaterDateIso, libelleQuestion, libelleValeur, optionsCible,
} from '../services/formulairesServices';

// Réponses d'un formulaire joint à un dossier (lot 5), en lecture seule : l'admin n'évalue rien, il affiche ce que
// le backend client a calculé à la dernière écriture (§3.3 du plan : `visibles`, `sections_visibles`, `manquantes`).
// Sections filtrées par `sections_visibles`, questions par `visibles`, paragraphes `info` ignorés ; une ligne
// « libellé : valeur lisible » par question (libellé d'option, Oui / Non, date jj/mm/aaaa, tableau « a, b », fichiers
// en liens vers fichiers_urls). Les ids hors `visibles` (questions masquées par une condition, avec leurs valeurs
// résiduelles et fichiers tant qu'ils ne sont pas purgés à la soumission) sont regroupés sous « Questions non posées ».
// Le bloc est repliable (Collapse), replié par défaut.

export interface ReponsesFormulaireProps {
  definition: Definition | null;            // snapshot de l'instance (null si la colonne JSON est illisible)
  reponses: Reponses | null;                // null tant que le client n'a rien enregistré
  statut: StatutInstance;                   // « — non renseigné » n'est marqué que si statut ≠ soumis
  fichiersUrls: Record<string, string>;     // nom réel du fichier -> URL publique (Instance.fichiers_urls)
}

// Taille lisible en ko (au moins 1 ko pour un fichier non vide)
const tailleKo = (octets: number): string => {
  const n = Number(octets);
  if (!Number.isFinite(n) || n <= 0) return '0 ko';
  return `${Math.max(1, Math.round(n / 1024))} ko`;
};

// Valeur brute -> texte lisible : libellé d'option (choix, choix_multiple, oui_non), date jj/mm/aaaa, texte / nombre
// tels quels ; un tableau (choix_multiple) devient « a, b ». Chaîne vide si rien n'est renseigné.
const texteValeur = (q: Question, v: ValeurReponse | undefined): string => {
  if (v === undefined || v === null) return '';
  const liste = Array.isArray(v) ? v : [v];
  const options = optionsCible(q);
  return liste
    .map((x) => {
      const s = String(x).trim();
      if (s === '') return '';
      if (options) return libelleValeur(q, s);
      if (q.type === 'date') return formaterDateIso(s);
      return s;
    })
    .filter((s) => s !== '')
    .join(', ');
};

// Même geste que « Détails du formulaire » (InfoFormulaire.handleMouseOver) : au survol, la valeur est sélectionnée
// d'un bloc pour être glissée-déposée telle quelle dans un autre champ ou une autre fenêtre.
const selectionnerAuSurvol = (event: React.MouseEvent<HTMLSpanElement>) => {
  const cible = event.currentTarget;
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.selectNodeContents(cible);
  selection.removeAllRanges();
  selection.addRange(range);
};

// Fichiers déposés pour une question (tableau vide si aucun)
const fichiersDe = (reponses: Reponses, id: string): FichierReponse[] => {
  const f = reponses.fichiers && reponses.fichiers[id];
  return Array.isArray(f) ? f : [];
};

// Ligne « libellé : valeur ». `marquerManquant` : question obligatoire visible d'un formulaire non soumis.
const Ligne: React.FC<{
  question: Question;
  reponses: Reponses;
  fichiersUrls: Record<string, string>;
  marquerManquant: boolean;
}> = ({ question: q, reponses, fichiersUrls, marquerManquant }) => {
  const fichiers = q.type === 'fichier' ? fichiersDe(reponses, q.id) : [];
  const texte = q.type === 'fichier' ? '' : texteValeur(q, reponses.valeurs ? reponses.valeurs[q.id] : undefined);
  const renseigne = q.type === 'fichier' ? fichiers.length > 0 : texte !== '';

  let valeur: React.ReactNode;
  if (!renseigne) {
    valeur = (
      <Typography component="span" variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
        {marquerManquant && q.obligatoire ? '— non renseigné' : '—'}
      </Typography>
    );
  } else if (q.type === 'fichier') {
    valeur = (
      <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.25 }}>
        {fichiers.map((f) => {
          const libelle = `${f.nom_original || f.nom} (${tailleKo(f.taille)})`;
          const url = fichiersUrls[f.nom];
          return url ? (
            <Link key={f.nom} href={url} target="_blank" rel="noopener" variant="body2" sx={{ wordBreak: 'break-all' }}>
              {libelle}
            </Link>
          ) : (
            <Typography key={f.nom} component="span" variant="body2" sx={{ wordBreak: 'break-all' }}>{libelle}</Typography>
          );
        })}
      </Box>
    );
  } else {
    valeur = (
      <Typography
        component="span"
        variant="body2"
        onMouseOver={selectionnerAuSurvol}
        title="Survolez pour sélectionner, puis glissez la valeur où vous voulez"
        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', userSelect: 'text', cursor: 'text' }}
      >
        {texte}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0, sm: 1 }, mb: 0.5, p: 0.5,
        userSelect: 'none', // seule la valeur est sélectionnable (au survol), pas le libellé
        '&:hover': { bgcolor: '#f0f0f0', borderRadius: 1 },
      }}
    >
      <Typography component="span" variant="body2" sx={{ fontWeight: 500, minWidth: { xs: 'auto', sm: '200px' }, flexShrink: 0 }}>
        {libelleQuestion(q)} :
      </Typography>
      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{valeur}</Box>
    </Box>
  );
};

const ReponsesFormulaire: React.FC<ReponsesFormulaireProps> = ({ definition, reponses, statut, fichiersUrls }) => {
  const [ouvert, setOuvert] = useState(false);

  if (!reponses) {
    return (
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
        Aucune réponse pour l'instant
      </Typography>
    );
  }
  if (!definition || !Array.isArray(definition.sections)) {
    return (
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'error.main' }}>
        Définition du formulaire illisible : réponses non affichables
      </Typography>
    );
  }

  const visibles = Array.isArray(reponses.visibles) ? reponses.visibles : [];
  const sectionsVisibles = Array.isArray(reponses.sections_visibles) ? reponses.sections_visibles : [];
  const marquerManquant = statut !== 'soumis';

  // Sections visibles avec leurs questions visibles (hors info)
  const sections = definition.sections
    .filter((s) => sectionsVisibles.indexOf(s.id) >= 0)
    .map((s) => ({ section: s, questions: s.questions.filter((q) => q.type !== 'info' && visibles.indexOf(q.id) >= 0) }))
    .filter((s) => s.questions.length > 0);

  // Questions non posées : hors visibles, hors info, toutes sections confondues (y compris les sections masquées)
  const nonPosees: Question[] = [];
  definition.sections.forEach((s) => {
    s.questions.forEach((q) => { if (q.type !== 'info' && visibles.indexOf(q.id) < 0) nonPosees.push(q); });
  });

  return (
    <Box>
      <Button
        size="small"
        onClick={() => setOuvert(!ouvert)}
        startIcon={ouvert ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ textTransform: 'none' }}
      >
        {ouvert ? 'Masquer les réponses' : 'Voir les réponses'}
      </Button>
      <Collapse in={ouvert} unmountOnExit>
        <Box sx={{ pl: 1, pt: 0.5 }}>
          {sections.length === 0 && (
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              Aucune question visible
            </Typography>
          )}
          {sections.map(({ section, questions }) => (
            <Box key={section.id} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{section.titre || section.id}</Typography>
              {questions.map((q) => (
                <Ligne key={q.id} question={q} reponses={reponses} fichiersUrls={fichiersUrls} marquerManquant={marquerManquant} />
              ))}
            </Box>
          ))}
          {nonPosees.length > 0 && (
            <details>
              <summary style={{ cursor: 'pointer', fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                Questions non posées ({nonPosees.length})
              </summary>
              <Box sx={{ pl: 1, pt: 0.5, opacity: 0.8 }}>
                {nonPosees.map((q) => (
                  <Ligne key={q.id} question={q} reponses={reponses} fichiersUrls={fichiersUrls} marquerManquant={false} />
                ))}
              </Box>
            </details>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ReponsesFormulaire;
